// Range slider with a number field for exact values.

import { useEffect, useState } from 'preact/hooks';
import { Field, fieldId } from './Field';

export interface SliderProps {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  unit?: string;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
/** Rounds to the step's decimals (0.1 + 0.2 style noise). */
const snap = (v: number, step: number) => {
  const decimals = (String(step).split('.')[1] ?? '').length;
  return Number(v.toFixed(decimals));
};

export function Slider({ label, hint, value, min, max, step, onChange, unit }: SliderProps) {
  const [id] = useState(() => fieldId('slider'));
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const fill = ((clamp(value, min, max) - min) / (max - min)) * 100;
  const commit = (raw: string) => {
    const v = Number(raw);
    if (raw.trim() !== '' && Number.isFinite(v)) onChange(snap(clamp(v, min, max), step));
    else setText(String(value));
  };
  return (
    <Field label={label} hint={hint} htmlFor={id}>
      <div class="slider">
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          style={{ '--fill': `${fill}%` }}
          onInput={(e) => onChange(snap(Number((e.currentTarget as HTMLInputElement).value), step))}
        />
        <div class="slider-number">
          <input
            type="number"
            class="input input-number"
            min={min}
            max={max}
            step={step}
            value={text}
            aria-label={`${label} value`}
            onInput={(e) => setText((e.currentTarget as HTMLInputElement).value)}
            onChange={(e) => commit((e.currentTarget as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === 'Enter' && commit((e.currentTarget as HTMLInputElement).value)}
          />
          {unit && <span class="slider-unit">{unit}</span>}
        </div>
      </div>
    </Field>
  );
}
